/* -----------------Globals--------------- */
import type { FlashList } from "@shopify/flash-list";
import { type RefObject, useCallback, useEffect, useState } from "react";

import { useAilaStore } from "../../hooks/useAilaStore";
/* -----------------Hooks--------------- */
import useSessionStore from "../../hooks/useSessionStore";

import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { trackAnswerEvent } from "@features/session/api";
import type { TrackAnswerEventRequest } from "@features/session/api/types";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { useDailyProgressStore } from "@hooks/useDailyProgressStore";
import { useDevice } from "@hooks/useDevice";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { getPlatform } from "@memorang/helpers/src/common";
import type { Confidence } from "@memorang/types/src/session/common";
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";
import Box from "@memorang/ui/src/components/Box";
import { ConfirmDialog } from "components/ConfirmDialog";
import { router, useLocalSearchParams } from "expo-router";
import { useTranslation } from "react-i18next";
import ConfettiCannon from "react-native-confetti-cannon";
import DialogSession from "../../components/SessionDialog";
import SessionEndDialog from "../../components/SessionEndDialog";
/* -----------------Components--------------- */
import StudySessionBottomAppBar from "../../components/StudySessionBottomAppBar";
import TutorialDialog from "../../components/dialogs/TutorialDialog";
import { tutorialDialogMetadata } from "../../components/dialogs/constants";
import {
	AI_SUBMITTABLE_VARIANTS,
	MAPPED_ITEM_TYPES,
	helpDialog,
} from "../../constants";
import type { DialogType } from "../../constants/dialog-configs";
import { getTotalScoreForAiItem } from "../../helpers/ai-feedback";
import useAIFeedbackStore from "../../hooks/useAIFeedbackStore";
import { useSession } from "../../hooks/useSession";
import BreakDialogContainer from "../BreakDialogContainer";
import ExpiredBreakDialogContainer from "../BreakDialogContainer/ExpiredBreakDialogContainer";
import {
	calculateAndUpdateProgress,
	getNextButtonStates,
	getShouldShowConfidencesSubmitButtons,
	getShowButtonNavFinish,
	getShowSubmitAnswerAIFeedbackButton,
	handlePostAnswer,
} from "./helpers";

interface Props {
	flashlistRef?: RefObject<FlashList<BaseItemSchema>>;
}
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const StudySessionBottombarContainer = ({ flashlistRef }: Props) => {
	const state = useSessionStore((state) => state);

	const { index } = useLocalSearchParams<{
		index: string;
	}>();

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const [tempSelectedConfidence, setTempSelectedConfidence] =
		useState<Confidence | null>(null);
	const [helpDialogVisible, setHelpDialogVisible] = useState(false);

	const {
		id,
		inReviewMode,
		isSummativeTest,
		currentBlockIndex,
		timeElapsed,
		sessionTime,
		updateProgress,
		numGoal,
		updateCurrentBlockIndex,
		numericId,
		toggleShowBreakDialog,
		answers,
		confettiCannons,
		updateShowConfetti,
		totalTime,
		sessionSubType,
		sessionItems,
		blockStates,
		itemStates,
		collectionId,
		showBreakDialog,
		serverExamBreak,
	} = state;

	const { endCurrentSession } = useSession();

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const hasAnsweredAtLeastOneItem = answers ? answers.size > 0 : false;

	const [startTime, setStartTime] = useState<Date>(new Date());
	const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);

	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);

	const toggleShowEndSessionDialog = () => {
		setShowEndSessionDialog((prev) => !prev);
	};

	const dailyProgressStore = useDailyProgressStore();

	const [showSessionDialogData, setShowSessionDialogData] = useState<{
		show: boolean;
		type?: DialogType;
	}>({
		show: false,
	});

	const {
		currentExamContext: { examId, examName },
	} = useExamContext();

	const updateShowDrawer = useAilaStore((state) => state.updateShowDrawer);

	const breaks = useSessionStore((state) => state.breaks);

	const lastBreak = breaks?.length ? breaks[breaks.length - 1] : null;

	const isBreakExpired = lastBreak?.status === "EXPIRED";

	const { isMobile } = useDevice();

	const isBreakOngoing = useSessionStore((state) => state.isBreakOngoing);

	const { currentItem, currentBlock, numSessionItems, numTotalItems } =
		useCurrentSession();

	const sectionId = currentItem.metadata?.section;

	const parentStem = currentItem?.stimulus?.content.passage?.paragraphs;
	const parentTitle = currentItem?.stimulus?.content.passage?.title;
	const itemType = currentItem?.metadata?.type;
	const mappedItemType = MAPPED_ITEM_TYPES[itemType!];
	const currentItemState = itemStates?.get(currentItem?.id!);

	const currentItemId = currentItem?.id!;
	const maximumScore =
		currentItem?.response?.maximumScore ?? getTotalScoreForAiItem(itemType!);
	const isAiSubmittable = AI_SUBMITTABLE_VARIANTS.includes(mappedItemType);

	const slideshowDurationInSeconds = useAIFeedbackStore(
		(state) => state.slideshowDurationInSecondsByItemId[currentItemId],
	);

	const {
		currentExamContext: { projectId, organizationId },
	} = useExamContext();

	const currentBlockState = blockStates.get(currentBlockIndex);
	const markedItems = currentBlockState?.markedItemsForReview;
	const highlightedItems = currentBlockState?.highlightedItems;
	const isMarked =
		currentItem?.answer?.marked || markedItems?.includes(currentItemId!);
	const highlightedHtml =
		currentItem?.answer?.highlight || highlightedItems?.get(currentItemId!);
	const handleUpdateItemStatePeriodically = useCallback(
		(time: number) => {
			const trackAnswerEventRequest: TrackAnswerEventRequest = {
				itemId: currentItemId!,
				itemVersionId: currentItem.versionId || 1,
				sessionId: Number(id),
				collectionId,
				projectId,
				organizationId,
				sessionType: "SUMMATIVE",
				itemDetails: {
					type: "ANSWER_EVENT",
					itemType: currentItem.metadata?.type!,
					variant: currentItem.metadata?.type!,
				},
				response: {
					selectedChoiceIds:
						currentItem.answer?.selectedChoiceIds ||
						currentItemState?.selectedChoiceIds ||
						[],
				},
				performance: {},
				settings: {},
				userInteraction: {
					highlight: highlightedHtml!,
					marked: isMarked!,
					sessionTime: time,
				},
				platform: getPlatform(),
			};
			trackAnswerEvent(trackAnswerEventRequest);
		},
		[
			currentItemId,
			id,
			collectionId,
			projectId,
			organizationId,
			isMarked,
			highlightedHtml,
			currentItem.metadata?.type,
			currentItem.versionId,
			currentItem.answer?.selectedChoiceIds,
			currentItemState?.selectedChoiceIds,
		],
	);

	useEffect(() => {
		if (
			timeElapsed &&
			timeElapsed % 300 === 0 &&
			!inReviewMode &&
			isSummativeTest &&
			!isBreakOngoing
		) {
			handleUpdateItemStatePeriodically(timeElapsed + (sessionTime || 0));
		}
	}, [
		handleUpdateItemStatePeriodically,
		inReviewMode,
		isSummativeTest,
		sessionTime,
		timeElapsed,
		isBreakOngoing,
	]);

	const hasShownConfidenceOnboardingDialog = useGlobalStore(
		(state) => state.hasShownConfidenceOnboardingDialog,
	);

	const setHasShownConfidenceOnboardingDialog = useGlobalStore(
		(state) => state.setHasShownConfidenceOnboardingDialog,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	useEffect(() => {
		setStartTime(new Date());
	}, [currentItemId]);

	const showConfidenceSubmitButtons = getShouldShowConfidencesSubmitButtons(
		state,
		currentItemIndex,
	);

	// next button states
	const {
		text: nextButtonText,
		disabled: disableNextButton,
		showButtonNavSkip,
	} = getNextButtonStates(state, currentItemIndex);
	const showButtonNavFinish = getShowButtonNavFinish(state, currentItemIndex);

	const sessionLength = currentBlock.length;
	const isLastItem = currentItemIndex === sessionLength - 1;

	const { t } = useTranslation();

	const prevButtonText = t("session.navigation.previous");

	const showPreviousButton = currentItemIndex > 0;

	const isLastBlock = isSummativeTest
		? currentBlockIndex === sessionItems.length - 1
		: false;

	const isLastItemInTheBlock = currentItemIndex === currentBlock.length - 1;

	useEffect(() => {
		if (currentItemIndex === 0) {
			const initProgress = 1 / numGoal!;
			updateProgress(initProgress);
		}
	}, [currentItemIndex, numGoal, updateProgress]);
	const handleUpdateAnswers = (confidence: Confidence) => {
		handlePostAnswer({
			state,
			startTime,
			confidence,
			dailyProgressState: dailyProgressStore,
			projectId,
			organizationId,
			examId,
			mappedItemType,
			submitAnswer: !isAiSubmittable,
			slideshowDurationInSeconds,
			currentItemIndex,
		});
	};

	const handleTrackAnswerEventAi = (confidence: Confidence) => {
		const aiResponse =
			useAIFeedbackStore.getState().aiResponse?.[currentItemId];
		handlePostAnswer({
			state,
			startTime,
			confidence,
			dailyProgressState: dailyProgressStore,
			projectId,
			organizationId,
			examId,
			mappedItemType,
			submitAnswer: true,
			aiFeedback: aiResponse,
			totalScore: maximumScore,
			slideshowDurationInSeconds,
			currentItemIndex,
		});
	};

	const onConfidenceProvided = (confidence: Confidence) => {
		if (hasShownConfidenceOnboardingDialog || isAiSubmittable) {
			handleUpdateAnswers(confidence);
		} else {
			setTempSelectedConfidence(confidence);
		}
	};

	const handlePrevious = () => {
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemNavigated,
			sessionId: id,
			itemId: currentItemId,
			type: "previous",
		});
		router.setParams({
			index: String(currentItemIndex),
			prev: "true",
		});
		calculateAndUpdateProgress(state, false);
	};

	useEffect(() => {
		if (!isWeb) {
			flashlistRef?.current?.scrollToIndex({
				index: currentItemIndex,
				animated: true,
			});
		}
	}, [currentItemIndex, flashlistRef]);
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const handleNext = () => {
		const eventParams = {
			index: currentItemIndex,
			itemId: currentItemId,
			sessionId: id,
			inReviewMode: String(inReviewMode),
		};
		if (isLastItemInTheBlock && !inReviewMode && isSummativeTest) {
			updateShowDrawer(false);
			const type = isLastBlock ? "completeTest" : "completeSection";
			if (isSummativeTest && !inReviewMode) {
				handleUpdateAnswers(50);
			}
			if (
				serverExamBreak &&
				serverExamBreak.section === sectionId &&
				serverExamBreak.status !== "EXPIRED" &&
				!isBreakExpired
			) {
				toggleShowBreakDialog(true);
			} else {
				setShowSessionDialogData({
					show: true,
					type,
				});
			}
			return;
		}
		if (showButtonNavFinish || isLastItem) {
			updateShowDrawer(false);
			explicitCallTrackCustomEvent({
				eventName: events.sessionFinishNavClicked,
				...eventParams,
			});
			if (inReviewMode) {
				router.back();
			} else if (hasAnsweredAtLeastOneItem) {
				endCurrentSession({
					id: id,
				});
				explicitCallTrackCustomEvent({
					eventName: events.sessionItemNavigated,
					...eventParams,
					type: "finish",
				});
				router.replace({
					pathname: "/(protected)/session/[id]/report",
					params: {
						id: id,
					},
				});
			} else {
				toggleShowEndSessionDialog();
			}
			return;
		}

		const newIndex = currentItemIndex + 1;

		router.setParams({
			index: String(newIndex + 1),
			prev: "false",
		});
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemNavigated,
			...eventParams,
			type: "next",
		});
		if (isSummativeTest && !inReviewMode) {
			handleUpdateAnswers(50);
		}
		calculateAndUpdateProgress(state, true);
	};

	const progressText = isSummativeTest
		? ""
		: `ITEM ${currentItemIndex + 1} / ${currentBlock.length}`;

	const handleCloseDialog = () => {
		setShowSessionDialogData({
			show: false,
		});
	};
	const moveToNextBlock = () => {
		if (isLastBlock) {
			endCurrentSession({
				id: id,
			});
			router.replace({
				pathname: "/(protected)/session/[id]/report",
				params: {
					id: numericId,
				},
			});
		} else {
			router.setParams({
				index: "1",
				type: "next",
			});
			updateCurrentBlockIndex(currentBlockIndex + 1);
		}
	};
	const handleClickActionButton = (type: string) => {
		if (type === "cancel") {
			handleCloseDialog();
		} else {
			handleCloseDialog();
			moveToNextBlock();
		}
	};

	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const audioResponse = useAIFeedbackStore((state) => state.audioResponse);
	const streaming = useAIFeedbackStore((state) => state.streaming);
	const showConfidenceOnboardingDialog = tempSelectedConfidence != null;
	const { show: showSubmitAnswerAIFeedbackButton } =
		getShowSubmitAnswerAIFeedbackButton({
			sessionState: state,
			currentItemIndex,
			currentUserResponse: userResponse?.[currentItemId],
			currentAudioResponse: audioResponse?.[currentItemId],
			shouldValidateAudio: false,
		});

	const hasParentStem =
		(parentStem && parentStem.length > 0) ||
		(parentTitle && parentTitle.length > 0 && !isMobile);

	const handleNavigation = (type: "positive" | "negative") => {
		if (type === "positive") {
			router.replace("/home");
		} else {
			toggleShowEndSessionDialog();
		}
	};

	const handleTrackConfidenceClick = (confidence: Confidence) => {
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemConfidenceClicked,
			confidence,
			itemId: currentItemId,
			sessionId: id,
		});
	};

	const showConfetti = confettiCannons.get(`${currentItemId}`);
	const numSections = numSessionItems;

	const confirmDialogConfig = helpDialog({
		sessionItems: currentBlock,
		numSections,
		examName,
		totalTime,
		isDiagnosticTest: sessionSubType === "DIAGNOSTIC",
		numTotalQuestions: numTotalItems,
		diagnosticLabel,
	});

	const toggleHelpDialog = () => {
		if (isMobile) {
			router.push({
				pathname: "/info",
				params: {
					title: confirmDialogConfig.title,
					body: confirmDialogConfig.body,
				},
			});
		} else {
			setHelpDialogVisible((prev) => !prev);
		}
	};

	return (
		<Box maxWidth={"100%"} alignSelf="center" width="100%">
			<StudySessionBottomAppBar
				handleNext={handleNext}
				disableNextButton={disableNextButton || streaming}
				nextButtonText={
					streaming ? t("session.navigation.next") : nextButtonText
				}
				handlePrevious={handlePrevious}
				hasParentStem={Boolean(hasParentStem)}
				handleTrackConfidenceClick={handleTrackConfidenceClick}
				disablePreviousButton={streaming}
				showSubmitAnswerAIFeedbackButton={showSubmitAnswerAIFeedbackButton}
				isSummativeTest={isSummativeTest}
				showButtonNavSkip={showButtonNavSkip && !streaming}
				showButtonPrevious={showPreviousButton}
				prevButtonText={prevButtonText}
				showButtonNavFinish={showButtonNavFinish}
				onConfidenceProvided={onConfidenceProvided}
				showConfidenceSubmitButtons={Boolean(showConfidenceSubmitButtons)}
				progressText={progressText}
				onAiFeedbackProvided={handleTrackAnswerEventAi}
				toggleHelpDialog={toggleHelpDialog}
			/>

			{showSessionDialogData.show && (
				<DialogSession
					type={showSessionDialogData.type}
					handleClickButton={handleClickActionButton}
					handleClose={handleCloseDialog}
					open={showSessionDialogData.show}
				/>
			)}
			{showConfidenceOnboardingDialog && (
				<TutorialDialog
					open={showConfidenceOnboardingDialog}
					handleClose={() => {
						if (tempSelectedConfidence) {
							handleUpdateAnswers(tempSelectedConfidence);
						}
						setTempSelectedConfidence(null);
						setHasShownConfidenceOnboardingDialog(true);
					}}
					{...tutorialDialogMetadata.confidence}
				/>
			)}
			{showEndSessionDialog && (
				<SessionEndDialog
					visible={showEndSessionDialog}
					onDismiss={toggleShowEndSessionDialog}
					handleAction={handleNavigation}
					hasAnsweredAtLeastOneItem={false}
				/>
			)}
			{showConfetti && (
				<ConfettiCannon
					onAnimationEnd={() => {
						updateShowConfetti({
							itemId: `${currentItemId}`,
							showConfetti: false,
						});
					}}
					count={200}
					origin={{ x: -10, y: 0 }}
					fallSpeed={1500}
				/>
			)}
			{helpDialogVisible && (
				<ConfirmDialog
					visible={helpDialogVisible}
					title={confirmDialogConfig.title}
					icon={confirmDialogConfig.icon}
					body={confirmDialogConfig.body}
					dismissCallback={() => setHelpDialogVisible(false)}
					confirmCallback={() => setHelpDialogVisible(false)}
					confirmCta="Close"
				/>
			)}

			{showBreakDialog && serverExamBreak?.allotedTime && (
				<BreakDialogContainer
					showBreakDialog={showBreakDialog}
					setShowBreakDialog={toggleShowBreakDialog}
					breakTime={serverExamBreak?.allotedTime}
					sectionId={sectionId!}
				/>
			)}
			{serverExamBreak?.allotedTime && (
				<ExpiredBreakDialogContainer
					setShowBreakDialog={toggleShowBreakDialog}
					breakTime={serverExamBreak?.allotedTime}
					sectionId={sectionId!}
					sessionId={id}
				/>
			)}
		</Box>
	);
};

export default StudySessionBottombarContainer;
